import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'

import type {
    DriverRemittance,
    DriverRemittanceStatus,
} from '@/lib/api/driver-cash-api'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {
    useConfirmRemittance,
    useDriverCashReport,
    useRejectRemittance,
    useRemittances,
    useVoidRemittance,
} from '@/hooks/use-driver-cash-query'
import { m } from '@/lib/paraglide/messages'
import { formatDateTimeShort, formatVnd } from '@/lib/format'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/accounting/remittances',
)({
    component: RemittancesPage,
})

function statusLabel(status: DriverRemittanceStatus): string {
    switch (status) {
        case 'pending':
            return m.driverRemittance_status_pending()
        case 'confirmed':
            return m.driverRemittance_status_confirmed()
        case 'rejected':
            return m.driverRemittance_status_rejected()
        case 'voided':
            return m.driverRemittance_status_voided()
    }
}

function statusVariant(
    status: DriverRemittanceStatus,
): 'default' | 'secondary' | 'destructive' | 'outline' {
    switch (status) {
        case 'confirmed':
            return 'default'
        case 'pending':
            return 'secondary'
        case 'rejected':
            return 'destructive'
        case 'voided':
            return 'outline'
    }
}

function RemittancesPage() {
    const { data, isLoading } = useRemittances({ per_page: 100 })
    const { data: cashReport } = useDriverCashReport()
    const confirmRemittance = useConfirmRemittance()
    const rejectRemittance = useRejectRemittance()
    const voidRemittance = useVoidRemittance()

    const [target, setTarget] = useState<DriverRemittance | null>(null)
    const [confirmedAmount, setConfirmedAmount] = useState('')
    const [reasonTarget, setReasonTarget] = useState<{
        r: DriverRemittance
        action: 'reject' | 'void'
    } | null>(null)
    const [reasonText, setReasonText] = useState('')

    const openConfirm = (r: DriverRemittance) => {
        setTarget(r)
        setConfirmedAmount(String(r.amount))
    }

    const openReason = (r: DriverRemittance, action: 'reject' | 'void') => {
        setReasonTarget({ r, action })
        setReasonText('')
    }

    const submitReason = () => {
        if (!reasonTarget) return
        const note = reasonText.trim()
        if (!note) return
        const opts = { onSuccess: () => setReasonTarget(null) }
        if (reasonTarget.action === 'reject') {
            rejectRemittance.mutate({ id: reasonTarget.r.id, note }, opts)
        } else {
            voidRemittance.mutate({ id: reasonTarget.r.id, note }, opts)
        }
    }

    const submitConfirm = () => {
        if (!target) return
        const value = Number(confirmedAmount)
        confirmRemittance.mutate(
            {
                id: target.id,
                data: {
                    confirmed_amount: Number.isFinite(value)
                        ? value
                        : undefined,
                },
            },
            { onSuccess: () => setTarget(null) },
        )
    }

    return (
        <div className="space-y-4">
            <h1 className="text-xl font-semibold">
                {m.driverRemittance_review_title()}
            </h1>

            <Card>
                <CardHeader>
                    <CardTitle className="text-base">
                        {m.driverRemittance_list_title()}
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {isLoading ? (
                        <p className="text-sm text-muted-foreground">
                            {m.common_loading()}
                        </p>
                    ) : (data?.data.length ?? 0) === 0 ? (
                        <p className="text-sm text-muted-foreground">
                            {m.driverRemittance_empty()}
                        </p>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>
                                        {m.driverRemittance_col_date()}
                                    </TableHead>
                                    <TableHead>
                                        {m.driverRemittance_col_driver()}
                                    </TableHead>
                                    <TableHead>
                                        {m.driverRemittance_amount()}
                                    </TableHead>
                                    <TableHead>
                                        {m.driverRemittance_col_confirmed()}
                                    </TableHead>
                                    <TableHead>
                                        {m.driverRemittance_col_status()}
                                    </TableHead>
                                    <TableHead>
                                        {m.driverRemittance_col_actions()}
                                    </TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {data?.data.map((r) => (
                                    <TableRow key={r.id}>
                                        <TableCell>
                                            {formatDateTimeShort(r.created_at)}
                                        </TableCell>
                                        <TableCell>
                                            {r.driver_name || r.driver_id}
                                        </TableCell>
                                        <TableCell>
                                            {formatVnd(r.amount)}
                                        </TableCell>
                                        <TableCell>
                                            {r.confirmed_amount != null
                                                ? formatVnd(r.confirmed_amount)
                                                : '—'}
                                        </TableCell>
                                        <TableCell>
                                            <Badge
                                                variant={statusVariant(
                                                    r.status,
                                                )}
                                            >
                                                {statusLabel(r.status)}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="space-x-2 whitespace-nowrap">
                                            {r.status === 'pending' && (
                                                <>
                                                    <Button
                                                        size="sm"
                                                        onClick={() =>
                                                            openConfirm(r)
                                                        }
                                                    >
                                                        {m.driverRemittance_confirm()}
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() =>
                                                            openReason(
                                                                r,
                                                                'reject',
                                                            )
                                                        }
                                                    >
                                                        {m.driverRemittance_reject()}
                                                    </Button>
                                                </>
                                            )}
                                            {r.status === 'confirmed' && (
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() =>
                                                        openReason(r, 'void')
                                                    }
                                                >
                                                    {m.driverRemittance_void()}
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            <Dialog
                open={target !== null}
                onOpenChange={(o) => !o && setTarget(null)}
            >
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {m.driverRemittance_confirm_title()}
                        </DialogTitle>
                    </DialogHeader>
                    {(() => {
                        const held = cashReport?.find(
                            (s) => s.driver_id === target?.driver_id,
                        )?.held
                        return held != null ? (
                            <p className="text-sm text-muted-foreground">
                                {m.driverRemittance_driver_held()}:{' '}
                                <span className="font-medium text-foreground">
                                    {formatVnd(held)}
                                </span>
                            </p>
                        ) : null
                    })()}
                    <div className="space-y-1">
                        <Label htmlFor="confirmed-amount">
                            {m.driverRemittance_confirmed_amount()}
                        </Label>
                        <Input
                            id="confirmed-amount"
                            type="number"
                            inputMode="numeric"
                            value={confirmedAmount}
                            onChange={(e) => setConfirmedAmount(e.target.value)}
                        />
                    </div>
                    <DialogFooter>
                        <Button
                            onClick={submitConfirm}
                            disabled={confirmRemittance.isPending}
                        >
                            {m.driverRemittance_confirm()}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog
                open={reasonTarget !== null}
                onOpenChange={(o) => !o && setReasonTarget(null)}
            >
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {reasonTarget?.action === 'void'
                                ? m.driverRemittance_void_title()
                                : m.driverRemittance_reject_title()}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-1">
                        <Label htmlFor="remittance-reason">
                            {m.driverRemittance_reason_label()}
                        </Label>
                        <Input
                            id="remittance-reason"
                            value={reasonText}
                            onChange={(e) => setReasonText(e.target.value)}
                            placeholder={m.driverRemittance_reason_placeholder()}
                        />
                    </div>
                    <DialogFooter>
                        <Button
                            variant="destructive"
                            onClick={submitReason}
                            disabled={
                                reasonText.trim() === '' ||
                                rejectRemittance.isPending ||
                                voidRemittance.isPending
                            }
                        >
                            {reasonTarget?.action === 'void'
                                ? m.driverRemittance_void()
                                : m.driverRemittance_reject()}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    )
}
